import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { IconChevronLeft, IconChevronRight, IconX } from "@tabler/icons-react";

import { cn } from "@/lib/utils";
import { Button } from "./Button/Button";

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

const DialogSelectTrigger = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Trigger> & {
    error?: boolean;
  }
>(({ className, children, error, ...props }, ref) => (
  <DialogPrimitive.Trigger
    ref={ref}
    className={cn(
      "flex h-10 w-full min-w-0 cursor-pointer items-center justify-between overflow-hidden rounded-md border border-input bg-white px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50 data-placeholder:text-muted-foreground",
      error && "border-destructive bg-destructive/2",
      className,
    )}
    {...props}
  >
    <span className="truncate">{children}</span>
    <IconChevronRight className="h-4 w-4 shrink-0 text-grey-400" />
  </DialogPrimitive.Trigger>
));
DialogSelectTrigger.displayName = DialogPrimitive.Trigger.displayName;

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "fixed inset-0 z-50 bg-black/30 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0",
        className,
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  classNameInner,
  classNameOverlay,
  children,
  showCloseButton = true,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  classNameInner?: string;
  classNameOverlay?: string;
  showCloseButton?: boolean;
}) {
  return (
    <DialogPortal>
      <DialogOverlay className={classNameOverlay} />
      <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center p-4">
        <DialogPrimitive.Content
          data-slot="dialog-content"
          className={cn(
            "pointer-events-auto relative flex max-h-full w-full flex-col gap-6 rounded-xl border border-input bg-popover text-sm text-popover-foreground drop-shadow-xl duration-100 outline-none data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
            className,
          )}
          {...props}
        >
          <div
            data-slot="dialog-content-inner"
            className={cn(
              "grid flex-1 gap-6 overflow-y-auto px-6 py-8 sm:px-8",
              classNameInner,
            )}
          >
            {children}
          </div>
          {showCloseButton && (
            <DialogPrimitive.Close data-slot="dialog-close" asChild>
              <Button
                variant="ghost"
                className="absolute top-2 right-2"
                size="iconSm"
              >
                <IconX />
                <span className="sr-only">Close</span>
              </Button>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </div>
    </DialogPortal>
  );
}

function DialogHeader({
  className,
  variant = "inline",
  showCloseButton = false,
  children,
  onBack,
  trailing,
  ...props
}: React.ComponentProps<"div"> & {
  variant?: "inline" | "bordered";
  onBack?: () => void;
  showCloseButton?: boolean;
  trailing?: React.ReactNode;
}) {
  const titleWrapperClassName = cn(
    "flex w-full flex-col items-start gap-1.5 text-start",
    variant === "bordered" &&
      "has-[[data-slot=dialog-description]:not(.sr-only)]:gap-0",
  );

  return (
    <div
      data-slot="dialog-header"
      className={cn(
        "flex flex-row items-center gap-1.5",
        variant === "bordered" &&
          "min-h-16 border-b border-border-primary px-7 py-4",
        variant === "bordered" &&
          "has-[[data-slot=dialog-description]:not(.sr-only)]:**:data-[slot=dialog-title]:text-base",
        !!showCloseButton && "pr-5",
        className,
      )}
      {...props}
    >
      <div className="flex flex-1 flex-row items-center gap-1.5">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="flex cursor-pointer flex-row items-center gap-3 text-text-primary"
          >
            <IconChevronLeft
              className="shrink-0 text-text-primary"
              width={18}
              height={18}
              strokeWidth={2.5}
            />
            <div className={titleWrapperClassName}>{children}</div>
          </button>
        ) : (
          <div className={titleWrapperClassName}>{children}</div>
        )}
      </div>
      {trailing}
      {showCloseButton && (
        <DialogPrimitive.Close asChild>
          <Button variant="ghost" size="iconSm">
            <IconX />
            <span className="sr-only">Close</span>
          </Button>
        </DialogPrimitive.Close>
      )}
    </div>
  );
}

function DialogFooter({
  className,
  variant = "inline",
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  variant?: "bordered" | "inline" | null;
  showCloseButton?: boolean;
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        variant === "bordered" && "border-t border-border-primary px-7 py-6",
        variant === "inline" && "mt-2 flex w-full justify-start gap-3",
        className,
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close asChild>
          <Button variant="outline">Close</Button>
        </DialogPrimitive.Close>
      )}
    </div>
  );
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      id="dialog-title"
      className={cn("w-full text-lg leading-6 font-semibold", className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      id="dialog-description"
      className={cn(
        "w-full text-sm wrap-break-word text-muted-foreground *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground",
        className,
      )}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  DialogSelectTrigger,
};
